import React, { useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CHECKOUT_URL } from '../data/salesContent';
import { trackInitiateCheckout } from '../utils/metaPixel';
type CarouselImage = {
  src: string;
  alt: string;
};
type ImageCarouselProps = {
  images: CarouselImage[];
  label: string;
};
export function ImageCarousel({
  images,
  label
}: ImageCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const goTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const idx = (i + images.length) % images.length;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({
      left: idx * el.clientWidth,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setActive(Math.round(el.scrollLeft / el.clientWidth));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(active - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(active + 1);
    }
  };
  const arrowClass = 'absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-700 bg-zinc-950/90 text-white transition-colors duration-150 ease-out hover:border-cyan-500 hover:bg-cyan-500 hover:text-zinc-950 focus:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/50';
  return <div role="region" aria-roledescription="carrusel" aria-label={label} onKeyDown={handleKeyDown} data-id="element-33" data-mp="xfs2shfnnaj4">
      <div className="relative" data-id="element-34" data-mp="sgxckufcyjoz">
        <div ref={trackRef} onScroll={handleScroll} className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-35" data-mp="shqp6c1003f1">
          {images.map((img, i) => <div key={img.src} role="group" aria-roledescription="diapositiva" aria-label={`${i + 1} de ${images.length}`} className="w-full shrink-0 snap-center px-12 sm:px-16" data-id="element-36" data-mp="2u8tzt1aw1hzl" data-mpi={i}>
              <a href={CHECKOUT_URL} onClick={trackInitiateCheckout} className="mx-auto block aspect-[4/5] w-full max-w-md overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 focus:outline-none focus-visible:ring-4 focus-visible:ring-cyan-400/50" data-id="element-37" data-mp="133qqo4ddqwzz">
                <img src={img.src} alt={img.alt} loading={i === 0 ? 'eager' : 'lazy'} className="h-full w-full object-contain" data-id="element-38" data-mp="1c600es0tqmcb" />
              </a>
            </div>)}
        </div>
        <button type="button" onClick={() => goTo(active - 1)} aria-label="Imagen anterior" className={`${arrowClass} left-0`} data-id="element-39" data-mp="1m1jur9zqpcyy">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-40" data-mp="1qvo2vwkqrr1m" />
        </button>
        <button type="button" onClick={() => goTo(active + 1)} aria-label="Imagen siguiente" className={`${arrowClass} right-0`} data-id="element-41" data-mp="1vs4rervus7f8">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-42" data-mp="1zsevfz034nrg" />
        </button>
      </div>
      <div className="mt-5 flex items-center justify-center gap-2" data-id="element-43" data-mp="24qzuvw7ij3eu">
        {images.map((img, i) => <button key={img.src} type="button" onClick={() => goTo(i)} aria-label={`Ir a la imagen ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${i === active ? 'w-7 bg-cyan-400' : 'w-2 bg-zinc-600 hover:bg-zinc-500'}`} data-id="element-44" data-mp="3p9ahxzw4pdlt" data-mpi={i} />)}
      </div>
      <p className="mt-2 text-center text-sm text-zinc-500" aria-live="polite" data-id="element-45" data-mp="bvp6sj9rq1wv">
        {active + 1} / {images.length}
      </p>
    </div>;
}